import React, { useEffect, useState } from 'react';
import { useTheme } from "@mui/material";
import { tokens } from "../../../theme";
import { Heading, Text } from "../../../components/DesignSystem/components/Typography";
import { Input } from "../../../components/DesignSystem/components/Input";
import { Button as DSButton } from "../../../components/DesignSystem/components/Button";
import { Chip } from "../../../components/DesignSystem/components/Chip";
import { ArrowLeft, ArrowRight, X, User, Users, Globe2, ScanFace, HeartHandshake, Martini} from "lucide-react";
import { useNavigate } from "react-router-dom";
import * as masterConstantActions from "../../../actions/masterConstant";
import * as userActions from "../../../actions/user";
import { API_URL } from "../../../api";
import { useCookies } from "../../../hooks/useCookies";
import { Spinner } from "react-bootstrap";
import { useRef } from "react";
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import BModal from 'react-bootstrap/Modal';

function SelectionModal({ show, onHide, title, options, selectedValue, onSelect, displayKey = "name", valueKey = "id" }) {
    if (!options || options.length === 0) return null;
    return (
        <BModal centered show={show} onHide={onHide} className="customer-modal-without-header-footer z-[9999]">
            <div className="p-[var(--ds-space-6)] max-h-[70vh] flex flex-col bg-white dark:bg-background-dark rounded-2xl border border-slate-100 dark:border-slate-800">
                <div className="flex justify-between items-center mb-[var(--ds-space-4)] shrink-0">
                    <Heading variant="h3" className="text-slate-900 dark:text-slate-100 font-bold">{title}</Heading>
                    <button onClick={onHide} className="text-slate-400 hover:text-slate-900 dark:hover:text-slate-100 transition-colors">
                        <X size={24} />
                    </button>
                </div>
                <div className="flex-1 overflow-y-auto custom-scrollbar pr-2 -mr-2">
                    <div className="flex flex-col gap-2 pb-2">
                        {options.map((opt) => {
                            const isSelected = selectedValue === opt[valueKey];
                            return (
                                <div 
                                    key={opt[valueKey]} 
                                    onClick={() => { onSelect(opt[valueKey]); onHide(); }}
                                    className={`p-4 rounded-xl cursor-pointer font-medium transition-all ${
                                        isSelected 
                                        ? "bg-brand/10 border-brand text-brand shadow-sm scale-[1.02]"
                                        : "bg-slate-50 dark:bg-slate-800 border-transparent text-slate-700 dark:text-slate-300 hover:border-brand/30 hover:bg-slate-100 dark:hover:bg-slate-700"
                                    } border-2 transition-all duration-300`}
                                >
                                    {opt[displayKey]}
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>
            {/* Inject minimal scrollbar styles for modal */}
            <style dangerouslySetInnerHTML={{__html: `
                .custom-scrollbar::-webkit-scrollbar { width: 4px; }
                .custom-scrollbar::-webkit-scrollbar-track { background: transparent; }
                .custom-scrollbar::-webkit-scrollbar-thumb { background: #ff794d33; border-radius: 10px; }
            `}} />
        </BModal>
    );
}

function MobileWheelHeightPickerModal({ show, onHide, title, options, selectedValue, onSelect, displayKey = "name", valueKey = "id" }) {
    const scrollRef = useRef(null);
    const [tempValue, setTempValue] = useState(selectedValue);

    useEffect(() => {
        if (show) {
            setTempValue(selectedValue);
            setTimeout(() => {
                if (scrollRef.current && options) {
                    let index = options.findIndex(o => o[valueKey] === selectedValue);
                    if (index === -1) index = 0;
                    scrollRef.current.scrollTop = index * 48;
                }
            }, 100);
        }
    }, [show, selectedValue, options]);

    const handleScroll = (e) => {
        const container = e.target;
        clearTimeout(container.scrollTimeout);
        container.scrollTimeout = setTimeout(() => {
            const index = Math.round(container.scrollTop / 48);
            if (options && options[index]) {
                setTempValue(options[index][valueKey]);
            }
        }, 50);
    };

    if (!options || options.length === 0) return null;

    return (
        <BModal 
            show={show} 
            onHide={onHide}
            centered
            dialogClassName="m-0 sm:m-auto w-full fixed bottom-0 sm:relative sm:max-w-md sm:bottom-auto z-[9999]"
            contentClassName="rounded-t-[32px] sm:rounded-3xl border-0 overflow-hidden bg-slate-50 dark:bg-slate-900" 
            animation={true}
        >
            <div className="p-6">
                <div className="flex justify-center items-center mb-6 relative">
                    <span className="text-sm font-semibold text-slate-500">{title}</span>
                    <button onClick={onHide} className="absolute right-0 text-slate-400 hover:text-slate-600 transition-colors">
                        <X size={20} />
                    </button>
                </div>

                <div className="relative h-[240px] overflow-hidden rounded-2xl bg-white dark:bg-slate-800">
                    {/* Highlighting bar */}
                    <div className="absolute top-[96px] left-4 right-4 h-[48px] rounded-xl bg-brand/10 dark:bg-brand/20 border border-brand/20 dark:border-brand/30 pointer-events-none z-0 shadow-inner"></div>

                    {/* Fixed Labels for visual flair */}
                    <div className="absolute top-[96px] right-8 h-[48px] flex items-center justify-end pointer-events-none z-20">
                    </div>

                    {/* Scrollable List */}
                    <div 
                        ref={scrollRef}
                        onScroll={handleScroll}
                        className="h-full overflow-y-auto snap-y snap-mandatory scroll-smooth relative z-10 picker-no-scrollbar"
                        style={{ paddingBottom: '96px', paddingTop: '96px' }}
                    >
                        {options.map((opt, i) => {
                            const isSelected = tempValue === opt[valueKey];
                            return (
                                <div 
                                    key={opt[valueKey]} 
                                    onClick={() => {
                                        if (scrollRef.current) scrollRef.current.scrollTo({ top: i * 48, behavior: 'smooth' });
                                        setTempValue(opt[valueKey]);
                                    }}
                                    className={`h-[48px] snap-center flex items-center justify-center text-lg transition-all duration-300 cursor-pointer px-12 text-center whitespace-nowrap ${isSelected ? 'text-brand font-black scale-110 drop-shadow-sm' : 'text-slate-400 dark:text-slate-500 bg-transparent'}`}
                                >
                                    {opt[displayKey]}
                                </div>
                            );
                        })}
                    </div>
                </div>

                {/* Hide Webkit scrollbar for wheel picker directly in JSX */}
                <style dangerouslySetInnerHTML={{__html: `
                    .picker-no-scrollbar::-webkit-scrollbar { display: none; }
                    .picker-no-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
                `}} />

                <div className="mt-8">
                    <button 
                        className="w-full bg-brand hover:brightness-110 text-white font-black rounded-2xl py-4 shadow-lg shadow-brand/20 transition-all uppercase tracking-widest text-sm"
                        onClick={() => {
                            onSelect(tempValue);
                            onHide();
                        }}
                    >
                        Save
                    </button>
                </div>
            </div>
        </BModal>
    );
}

export const Lifestyle = () => {
    const navigate = useNavigate();
    
    // 1. Theme Configuration Hook
    const theme = useTheme();
    const mode = theme.palette.mode;
    
    // 2. FOUC Tracker State
    const [isTailwindLoaded, setIsTailwindLoaded] = useState(!!window.tailwind);

    // 3. Centralized FOUC Prevention & Engine Mount
    useEffect(() => {
        const applyConfig = () => {
            const modeColors = tokens(mode);
            if (window.tailwind) {
                window.tailwind.config = {
                    darkMode: "class",
                    theme: {
                        extend: {
                            colors: {
                                "brand": modeColors.greenAccent[500] || "#ff794d",
                                "background-light": mode === "dark" ? modeColors.primary[500] : "#f8f6f5",
                                "background-dark": modeColors.primary[500] || "#23140f",
                                "action-black": "#111111",
                            },
                            fontFamily: {
                                "display": ["Inter", "sans-serif"],
                                "body": ['"Source Sans Pro"', "sans-serif"],
                            },
                        },
                    },
                };
            }
        };

        if (window.tailwind) {
            applyConfig();
            setIsTailwindLoaded(true);
        } else {
            const script = document.createElement("script");
            script.src = "https://cdn.tailwindcss.com?plugins=forms";
            script.onload = () => {
                applyConfig();
                setIsTailwindLoaded(true);
            };
            document.head.appendChild(script);
        }
    }, [mode]);

    const { getCookie } = useCookies();
    const token = getCookie("token");

    const [isMobile, setIsMobile] = useState(false);
    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth < 768);
        handleResize();
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const [loading, setLoading] = useState(false);
    const [errorMsg, setErrorMsg] = useState("");

    // Smoke and Drink
    const smokeOptions = ["Yes", "No", "Sometimes", "Prefer not to say"];
    const [selectedSmoke, setSelectedSmoke] = useState(null);
    const [selectedDrink, setSelectedDrink] = useState(null);

    // Education
    const [studiesList, setStudiesList] = useState([]);
    const [selectedEducation, setSelectedEducation] = useState(null);

    // Job Title
    const [jobTypes, setJobTypes] = useState([]);
    const [selectedJob, setSelectedJob] = useState("");

    // Height
    const [heights, setHeights] = useState([]);
    const [selectedHeight, setSelectedHeight] = useState("");

    // Religion
    const [allReligions, setAllReligions] = useState([]);
    const [selectedReligion, setSelectedReligion] = useState("");

    // Languages
    const [allLanguages, setAllLanguages] = useState([]);
    const [selectedLanguages, setSelectedLanguages] = useState([]);

    // Modal State
    const [modalConfig, setModalConfig] = useState({ show: false, type: null });

    const isFetched = useRef(false);

    useEffect(() => {
        if (isFetched.current || !token) return;
        isFetched.current = true;

        const fetchData = async () => {
            try {
                // Fetch constant options
                masterConstantActions.getConstantStudies().then(res => res.status && setStudiesList(res.data));
                masterConstantActions.getConstantJobs().then(res => res.status && setJobTypes(res.data));
                masterConstantActions.getConstantGrowths().then(res => res.status && setHeights(res.data));
                
                // Religions
                fetch(`${API_URL}customer/users/religions`, { headers: { Authorization: `Bearer ${token}` } })
                    .then(res => res.json())
                    .then(data => {
                        setAllReligions(data.religions || []);
                        if (data.userReligion) setSelectedReligion(data.userReligion.name);
                    });

                // Languages
                fetch(`${API_URL}customer/users/language`, { headers: { Authorization: `Bearer ${token}` } })
                    .then(res => res.json())
                    .then(data => setAllLanguages(data.languages || []));

                // Fetch user data
                // Smoke/Drink
                fetch(`${API_URL}customer/users/smoke-drink`, { headers: { Authorization: `Bearer ${token}` } })
                    .then(res => res.json())
                    .then(data => {
                        if (data.smoke_id) setSelectedSmoke(smokeOptions[data.smoke_id - 1]);
                        if (data.drink_id) setSelectedDrink(smokeOptions[data.drink_id - 1]);
                    });
                
                // Education
                userActions.getUserStudies().then(res => setSelectedEducation(res?.studies?.[0]?.id));
                
                // Jobs
                userActions.getUserJobs().then(res => res?.jobs?.length && setSelectedJob(res.jobs[0].name));

                // Heights
                userActions.getUserGrowths().then(res => setSelectedHeight(res?.growths?.[0]?.id));

                // Selected Languages
                fetch(`${API_URL}customer/users/userlanguage`, { headers: { Authorization: `Bearer ${token}` } })
                    .then(res => res.json())
                    .then(data => setSelectedLanguages(data.selectedLanguages?.map(l => l.language_id) || []));
                
            } catch (error) {
                console.error("Error fetching data:", error);
            }
        };

        fetchData();
    }, [token]);

    const handleSaveAll = async () => {
        setErrorMsg("");
        if (!selectedSmoke || !selectedDrink || !selectedEducation || !selectedJob || !selectedHeight || !selectedReligion || selectedLanguages.length === 0) {
            setErrorMsg("Please fill out all fields before proceeding.");
            return;
        }

        setLoading(true);
        // Navigate instantly for smooth UX
        navigate('/personality');

        try {
            // Gather all API requests
            const promises = [];

            // Smoke / Drink
            promises.push(fetch(`${API_URL}customer/users/update-smoke`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                body: JSON.stringify({ smoke_id: smokeOptions.indexOf(selectedSmoke) + 1 })
            }));
            promises.push(fetch(`${API_URL}customer/users/update-drink`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                body: JSON.stringify({ drink_id: smokeOptions.indexOf(selectedDrink) + 1 })
            }));

            // Education
            promises.push(userActions.UpdateUserStudies({ id: selectedEducation }));

            // Job Title
            promises.push(userActions.updateUserJobs({ name: selectedJob }));

            // Height
            promises.push(userActions.UpdateUserGrowths({ growthId: selectedHeight }));

            // Religion
            const religionId = allReligions.find(r => r.name === selectedReligion)?.id;
            if (religionId) {
                promises.push(fetch(`${API_URL}customer/users/religions`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                    body: JSON.stringify({ religionId })
                }));
                promises.push(fetch(`${API_URL}customer/user/preferences/save/religion_id`, {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                    body: JSON.stringify({ value: religionId })
                }));
            }

            // Languages
            if (selectedLanguages.length > 0) {
                promises.push(fetch(`${API_URL}customer/users/language`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                    body: JSON.stringify({ languages: selectedLanguages })
                }));
            }

            // Execute all requests in the background 
            Promise.all(promises).catch((error) => {
                console.error("Failed to save some lifestyle information in background", error);
            });

        } catch (error) {
            console.error("Error executing update logic: ", error);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div 
            className={`${mode === "dark" ? "dark" : ""} bg-background-light dark:bg-background-dark font-display text-slate-900 dark:text-slate-100 antialiased min-h-[100dvh] lg:h-screen lg:overflow-hidden w-full`}
            style={{ opacity: isTailwindLoaded ? 1 : 0, transition: 'opacity 0.1s ease-out' }}
        >
            <div className="flex flex-col min-h-[100dvh] lg:h-screen w-full overflow-hidden bg-white dark:bg-background-dark">
                
                {/* Main Content Area (Full Screen, Centered Form) */}
                <div className="w-full flex-1 flex flex-col min-h-[100dvh] lg:h-screen overflow-y-auto custom-scrollbar relative">
                    
                    {/* Header/Nav */}
                    <header className="sticky top-0 z-30 bg-background-light/90 dark:bg-background-dark/90 backdrop-blur-md px-[var(--ds-space-6)] py-[var(--ds-space-4)] flex items-center justify-between border-b border-brand/10">
                        <button type="button" onClick={() => navigate(-1)} className="flex items-center justify-center h-10 w-10 shrink-0 rounded-full bg-brand/10 text-action-black dark:text-white hover:bg-brand/20 transition-colors" aria-label="Go back">
                            <ArrowLeft className="shrink-0" size={24} />
                        </button>
                        <div className="flex items-center gap-[var(--ds-space-2)]">
                            <div className="h-2 w-24 bg-brand/20 rounded-full overflow-hidden">
                                <div className="h-full bg-brand" style={{ width: '83%' }}></div>
                            </div>
                            <span className="text-xs font-bold uppercase tracking-wider text-brand">Step 5 of 6</span>
                        </div>
                        <button type="button" className="flex items-center justify-center h-10 w-10 shrink-0 rounded-full bg-brand/10 text-action-black dark:text-white hover:bg-brand/20 transition-colors" aria-label="Close">
                            <X className="shrink-0" size={24} />
                        </button>
                    </header>

                    <main className="flex-1 px-[var(--ds-space-6)] py-[var(--ds-space-6)] lg:px-[var(--ds-space-12)] lg:py-[var(--ds-space-8)] max-w-6xl mx-auto w-full flex flex-col items-start text-left pb-40 lg:pb-[var(--ds-space-8)]">
                        <div className="mb-[var(--ds-space-8)] w-full text-left">
                            <Heading variant="h1" className="ds-heading-page-title text-slate-900 dark:text-slate-100 mb-[var(--ds-space-2)] text-left">
                                A few more details
                            </Heading>
                            <Text as="p" className="ds-text-page-subtitle text-slate-500 dark:text-slate-400 text-left">
                                Tell us about your lifestyle and background to find better matches.
                            </Text>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-[var(--ds-space-4)] md:gap-[var(--ds-space-6)] pb-[80px] w-full items-start">
                            {/* Smoking */}
                            <div className="bg-slate-50 dark:bg-slate-800/30 p-4 md:p-5 rounded-2xl border border-slate-200 dark:border-slate-700/80 transition-all col-span-1">
                                <label className="block text-sm font-semibold text-slate-500 dark:text-slate-400 mb-3">Smoking</label>
                                <div className="flex flex-wrap gap-2">
                                    {smokeOptions.map((type) => (
                                        <Chip 
                                            key={type}
                                            label={type}
                                            selected={selectedSmoke === type}
                                            onClick={() => setSelectedSmoke(type)}
                                        />
                                    ))}
                                </div>
                            </div>

                            {/* Drinking */}
                            <div className="bg-slate-50 dark:bg-slate-800/30 p-4 md:p-5 rounded-2xl border border-slate-200 dark:border-slate-700/80 transition-all col-span-1">
                                <label className="block text-sm font-semibold text-slate-500 dark:text-slate-400 mb-3">Drinking</label>
                                <div className="flex flex-wrap gap-2">
                                    {smokeOptions.map((type) => (
                                        <Chip 
                                            key={type}
                                            label={type}
                                            selected={selectedDrink === type}
                                            onClick={() => setSelectedDrink(type)}
                                        />
                                    ))}
                                </div>
                            </div>

                            {/* Height */}
                            <div className="bg-slate-50 dark:bg-slate-800/30 p-4 md:p-5 rounded-2xl border border-slate-200 dark:border-slate-700/80 cursor-pointer group transition-all col-span-1" onClick={() => setModalConfig({ show: true, type: 'height' })}>
                                <label className="block text-sm font-semibold text-slate-500 dark:text-slate-400 mb-2 cursor-pointer">Height</label>
                                <div className="flex items-center justify-between text-sm p-3 rounded-lg bg-white dark:bg-slate-900/50 border border-slate-200 dark:border-slate-700 group-hover:border-slate-300 transition-colors">
                                    <span className={selectedHeight ? "text-slate-900 dark:text-slate-100 font-medium whitespace-nowrap overflow-hidden text-ellipsis" : "text-slate-400"}>
                                        {selectedHeight ? heights.find(h => h.id === selectedHeight)?.name : "Select Height"}
                                    </span>
                                    <ArrowRight size={18} className="text-slate-300 group-hover:text-slate-400 transition-colors shrink-0" />
                                </div>
                            </div>

                            {/* Education */}
                            <div className="bg-slate-50 dark:bg-slate-800/30 p-4 md:p-5 rounded-2xl border border-slate-200 dark:border-slate-700/80 cursor-pointer group transition-all col-span-1" onClick={() => setModalConfig({ show: true, type: 'education' })}>
                                <label className="block text-sm font-semibold text-slate-500 dark:text-slate-400 mb-2 cursor-pointer">Education</label>
                                <div className="flex items-center justify-between text-sm p-3 rounded-lg bg-white dark:bg-slate-900/50 border border-slate-200 dark:border-slate-700 group-hover:border-slate-300 transition-colors">
                                    <span className={selectedEducation ? "text-slate-900 dark:text-slate-100 font-medium whitespace-nowrap overflow-hidden text-ellipsis" : "text-slate-400"}>
                                        {selectedEducation ? studiesList.find(s => s.id === selectedEducation)?.name : "Select Education"}
                                    </span>
                                    <ArrowRight size={18} className="text-slate-300 group-hover:text-slate-400 transition-colors shrink-0" />
                                </div>
                            </div>

                            {/* Job Title */}
                            <div className="bg-slate-50 dark:bg-slate-800/30 p-4 md:p-5 rounded-2xl border border-slate-200 dark:border-slate-700/80 transition-all group md:col-span-2">
                                <label className="block text-sm font-semibold text-slate-500 dark:text-slate-400 mb-2">Job Title</label>
                                <div className="rounded-lg bg-white dark:bg-slate-900/50 border border-slate-200 dark:border-slate-700 transition-colors">
                                    <Autocomplete
                                        freeSolo
                                        options={jobTypes.map((option) => option.name)}
                                        value={selectedJob}
                                        onChange={(event, newValue) => {
                                            setSelectedJob(newValue);
                                        }}
                                        onInputChange={(event, newInputValue) => {
                                            setSelectedJob(newInputValue);
                                        }}
                                        renderInput={(params) => (
                                            <TextField 
                                                {...params} 
                                                placeholder="e.g. Creative Director"
                                                variant="standard"
                                                InputProps={{
                                                    ...params.InputProps,
                                                    disableUnderline: true,
                                                    className: "w-full text-sm font-medium dark:text-slate-200 p-3 px-4 focus:outline-none focus:ring-0 !border-none",
                                                }}
                                                inputProps={{
                                                    ...params.inputProps,
                                                    className: "w-full focus:outline-none focus:ring-0 !border-none !ring-0 !shadow-none bg-transparent p-0 m-0",
                                                }}
                                            />
                                        )}
                                    />
                                </div>
                            </div>

                            {/* Religion */}
                            <div className="bg-slate-50 dark:bg-slate-800/30 p-4 md:p-5 rounded-2xl border border-slate-200 dark:border-slate-700/80 transition-all md:col-span-2">
                                <label className="block text-sm font-semibold text-slate-500 dark:text-slate-400 mb-3">Religion</label>
                                <div className="flex flex-wrap gap-2">
                                    {allReligions?.map((r, i) => (
                                        <Chip 
                                            key={r.id || i}
                                            label={r.name}
                                            selected={selectedReligion === r.name}
                                            onClick={() => setSelectedReligion(r.name)}
                                        />
                                    ))}
                                </div>
                            </div>

                            {/* Languages Spoken */}
                            <div className="bg-slate-50 dark:bg-slate-800/30 p-4 md:p-5 rounded-2xl border border-slate-200 dark:border-slate-700/80 transition-all md:col-span-2">
                                <label className="block text-sm font-semibold text-slate-500 dark:text-slate-400 mb-3">Languages Spoken</label>
                                <div className="flex flex-wrap gap-2">
                                    {allLanguages?.map((lang) => (
                                        <Chip 
                                            key={lang.id}
                                            label={lang.name}
                                            selected={selectedLanguages.includes(lang.id)}
                                            onClick={() => {
                                                setSelectedLanguages(prev => 
                                                    prev.includes(lang.id) ? prev.filter(id => id !== lang.id) : [...prev, lang.id]
                                                );
                                            }}
                                        />
                                    ))}
                                </div>
                            </div>
                        </div>

                    </main>

                    {/* Fixed Sticky Footer for CTAs */}
                    <div className="fixed bottom-0 left-0 right-0 bg-background-light/90 dark:bg-background-dark/95 backdrop-blur-md border-t border-slate-200 dark:border-slate-800 p-4 lg:p-6 z-50 w-full lg:static lg:bg-transparent lg:backdrop-blur-none lg:border-none lg:mt-auto">
                        {errorMsg && (
                            <div className="mb-4 max-w-6xl mx-auto p-3 rounded-lg bg-red-100 border border-red-300 text-red-700 text-sm text-center">
                                {errorMsg}
                            </div>
                        )}
                        <div className="flex flex-col md:flex-row justify-center items-center gap-4 max-w-6xl mx-auto">
                            <DSButton 
                                type="button"
                                variant="secondary"
                                onClick={() => navigate('/personality')}
                                disabled={loading}
                                className="w-full md:w-[180px]"
                            >
                                Ask me later
                            </DSButton>
                            <DSButton 
                                type="button"
                                variant="cta"
                                onClick={handleSaveAll}
                                endIcon={!loading && <ArrowRight size={20} />}
                                disabled={loading}
                                className="w-full md:w-[180px]"
                            >
                                {loading ? <Spinner animation="border" size="sm" /> : "Next Step"}
                            </DSButton>
                        </div>
                    </div>
                </div>
            </div>
            <SelectionModal 
                show={modalConfig.show && modalConfig.type === 'education'} 
                onHide={() => setModalConfig({ show: false, type: null })}
                title="Your Education"
                options={studiesList}
                selectedValue={selectedEducation}
                onSelect={(val) => setSelectedEducation(val)}
                displayKey="name"
                valueKey="id"
            />
            {isMobile ? (
                <MobileWheelHeightPickerModal 
                    show={modalConfig.show && modalConfig.type === 'height'} 
                    onHide={() => setModalConfig({ show: false, type: null })}
                    title="Edit Height"
                    options={heights}
                    selectedValue={selectedHeight}
                    onSelect={(val) => setSelectedHeight(val)}
                    displayKey="name"
                    valueKey="id"
                />
            ) : (
                <SelectionModal 
                    show={modalConfig.show && modalConfig.type === 'height'} 
                    onHide={() => setModalConfig({ show: false, type: null })}
                    title="Your Height"
                    options={heights}
                    selectedValue={selectedHeight}
                    onSelect={(val) => setSelectedHeight(val)}
                    displayKey="name"
                    valueKey="id"
                />
            )}
            {/* Inject minimal scrollbar styles for this component */}
            <style dangerouslySetInnerHTML={{__html: `
                .custom-scrollbar::-webkit-scrollbar { width: 4px; }
                .custom-scrollbar::-webkit-scrollbar-track { background: transparent; }
                .custom-scrollbar::-webkit-scrollbar-thumb { background: #ff794d33; border-radius: 10px; }
            `}} />
        </div>
    );
};
